import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /ecommerce-marketing-agency — Sunday Digital
   Built on V5's design system. New signature moments unique
   to this page: the rotating concentric "full circle" hero
   ring, the staggered editorial 8-services grid with hover
   reveal media tiles, and the showreel band. The MarketingPinnedV5
   from the homepage is also embedded as a familiar moment, the
   way HeroVideoV2 was reused on Shopify Dev.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-7 py-4 rounded-md transition-colors"
  >
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </button>
);

/* ============================================================
   01 · Hero — full-circle rotating ring (signature for this page)
   8 service nodes orbit a slow-rotating ring in the right column.
   Mobile: ring becomes a static SVG with same node positions.
============================================================ */
const SERVICE_NODES = [
  "Strategy",
  "Content",
  "Social",
  "Email",
  "Influencer",
  "Paid Ads",
  "SEO",
  "CRO",
];

const FullCircleRing = () => {
  // Ring builder — 8 nodes evenly distributed.
  const radius = 200; // px in viewBox units
  const cx = 250;
  const cy = 250;
  return (
    <div className="relative w-full aspect-square max-w-[28rem] mx-auto">
      <motion.svg
        viewBox="0 0 500 500"
        className="absolute inset-0 w-full h-full"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 70, ease: "linear", repeat: Infinity }}
        style={{ originX: "50%", originY: "50%" }}
      >
        {/* Outer ring — hairline */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.10)"
          strokeWidth="1"
        />
        {/* Mid ring — dotted yellow */}
        <circle
          cx={cx}
          cy={cy}
          r={radius - 30}
          fill="none"
          stroke="rgba(244,214,78,0.35)"
          strokeWidth="1"
          strokeDasharray="2 6"
        />
        {/* Inner ring — solid faint */}
        <circle
          cx={cx}
          cy={cy}
          r={radius - 70}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth="1"
        />

        {/* Service nodes on outer ring */}
        {SERVICE_NODES.map((label, i) => {
          const angle = (i / SERVICE_NODES.length) * Math.PI * 2 - Math.PI / 2;
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          return (
            <g key={label}>
              <circle cx={x} cy={y} r="6" fill="#F4D64E" />
              <circle
                cx={x}
                cy={y}
                r="11"
                fill="none"
                stroke="rgba(244,214,78,0.4)"
                strokeWidth="1"
              />
            </g>
          );
        })}
      </motion.svg>

      {/* Counter-rotating labels — keep upright while ring rotates.
          Each label is a clickable button that smooth-scrolls to its
          matching service grid card below. The 1:1 index mapping between
          SERVICE_NODES and SERVICES means node `i` deep-links to service
          `String(i+1).padStart(2,"0")`. */}
      <motion.div
        className="absolute inset-0"
        initial={{ rotate: 0 }}
        animate={{ rotate: -360 }}
        transition={{ duration: 70, ease: "linear", repeat: Infinity }}
      >
        {SERVICE_NODES.map((label, i) => {
          const angle = (i / SERVICE_NODES.length) * Math.PI * 2 - Math.PI / 2;
          // Labels positioned slightly outside the ring
          const labelRadiusPct = 47; // % from centre — outside the outer ring
          const x = 50 + Math.cos(angle) * labelRadiusPct;
          const y = 50 + Math.sin(angle) * labelRadiusPct;
          const serviceId = `service-${String(i + 1).padStart(2, "0")}`;
          const handleClick = () => {
            const el = document.getElementById(serviceId);
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            // Briefly highlight the target card so the user sees the link landed
            el.classList.add("ring-target-pulse");
            window.setTimeout(
              () => el.classList.remove("ring-target-pulse"),
              1400
            );
          };
          return (
            <span
              key={label}
              className="absolute -translate-x-1/2 -translate-y-1/2 whitespace-nowrap"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              {/* Counter-rotate again so the label itself stays static
                  relative to the page (compensates the parent rotation). */}
              <motion.span
                className="inline-block"
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{
                  duration: 70,
                  ease: "linear",
                  repeat: Infinity,
                }}
              >
                <button
                  type="button"
                  onClick={handleClick}
                  data-testid={`ring-node-${String(i + 1).padStart(2, "0")}`}
                  aria-label={`Jump to ${label} service`}
                  className="group relative inline-flex items-center text-[0.6rem] sm:text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-300 hover:text-[#F4D64E] focus:text-[#F4D64E] focus:outline-none transition-colors px-2 py-1 -mx-2 -my-1 rounded-md cursor-pointer"
                >
                  {label}
                  <span
                    aria-hidden
                    className="absolute -bottom-1 left-2 right-2 h-px bg-[#F4D64E] origin-center scale-x-0 group-hover:scale-x-100 group-focus:scale-x-100 transition-transform duration-300"
                  />
                </button>
              </motion.span>
            </span>
          );
        })}
      </motion.div>

      {/* Centre badge */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className="font-display text-[2.5rem] md:text-[3rem] font-black text-[#F4D64E] tabular-nums leading-none">
            360°
          </div>
          <div className="mt-2 text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
            Full circle
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketingHero = ({ onPrimary }) => {
  const cms = useContent("marketing");
  const h = (cms && cms.hero) || {};
  const breadcrumb = h.breadcrumb || "eCommerce Marketing";
  const eyebrow = h.eyebrow || "360 Digital Marketing";
  const titleLine1 = h.title_line_1 || "Full circle.";
  const titleLine2 = h.title_line_2 || "Built for";
  const titleEmphasis = h.title_emphasis || "growth";
  const titleAccent = h.title_accent || "";
  const subtitle = h.subtitle ||
    "A boutique marketing team turning ideas into traffic, traffic into customers, and customers into community — strategy, creative, media, and analytics under one roof.";
  const ctaLabel = h.cta_primary_label || h.cta_label || "Start a Project";
  const secondaryLabel = h.cta_secondary_label || "See services";
  const secondaryHref = h.cta_secondary_href || "#services";
  return (
  <section
    id="top"
    data-testid="marketing-hero"
    className="relative overflow-hidden bg-[#0B0F14]"
  >
    {/* Editorial yellow glow */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(900px 600px at 80% 30%, rgba(244,214,78,0.10), transparent 60%)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
      {/* Breadcrumb */}
      <div
        data-reveal="eyebrow"
        className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-10 md:mb-14"
      >
        <a href="/" className="hover:text-[#F4D64E] transition-colors">Sunday</a>
        <span className="w-1 h-1 rounded-full bg-neutral-700" />
        <span className="text-[#F4D64E]">{breadcrumb}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-16 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-7">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h1
            data-testid="marketing-hero-title"
            className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[4.75rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
          >
            <span data-reveal="mask-block">
              <span>{titleLine1}</span>
            </span>
            <span data-reveal="mask-block" style={{ "--i": 1 }} className="block">
              <span>
                {titleLine2}{" "}
                {titleEmphasis ? (
                  <span className="italic font-normal text-neutral-400">{titleEmphasis}</span>
                ) : null}
                {titleAccent ? (
                  <>
                    {" "}
                    <PillarAccent>{titleAccent}</PillarAccent>
                  </>
                ) : null}
                <span className="text-white">.</span>
              </span>
            </span>
          </h1>
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="mt-9 max-w-[52ch] text-[1.02rem] md:text-[1.15rem] text-neutral-300 leading-[1.6]"
          >
            <span>
              {subtitle}
            </span>
          </p>
          <div
            data-reveal="eyebrow"
            style={{ "--i": 3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <PrimaryCTA testId="mkt-hero-cta" onClick={onPrimary}>
              {ctaLabel}
            </PrimaryCTA>
            <a
              href={secondaryHref}
              className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
            >
              {secondaryLabel}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>

        {/* RIGHT — full circle ring */}
        <div className="lg:col-span-5">
          <FullCircleRing />
        </div>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   02 · Trust strip
============================================================ */
const TrustStripMkt = () => {
  const cms = useContent("marketing");
  const liveItems = Array.isArray(cms?.trust_strip?.items) && cms.trust_strip.items.length
    ? cms.trust_strip.items.map((it) => it.title || "").filter(Boolean)
    : ["In-house team", "Data-led", "360° stack", "10+ years", "Brooklyn · Remote"];
  const lastIdx = liveItems.length - 1;
  return (
    <section className="relative bg-[#0B0F14] border-y border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-7">
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
          {liveItems.map((label, i) => (
            <span key={`${label}-${i}`} className="flex items-center gap-x-8">
              <span className={i === lastIdx ? "text-[#F4D64E]" : ""}>{label}</span>
              {i < lastIdx ? (
                <span aria-hidden className="hidden md:inline w-1 h-1 rounded-full bg-neutral-700" />
              ) : null}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   03 · Why us — twin column editorial paragraph
============================================================ */
const WhyUs = () => {
  const cms = useContent("marketing");
  const w = cms?.why_us || {};
  const eyebrow = w.eyebrow || "The Sunday way";
  const headlineMain = w.headline_main || "Strategy meets";
  const headlineItalic = w.headline_italic || "execution";
  const headlineMiddle = w.headline_middle ?? "";
  const headlineAccent = w.headline_accent || "Data";
  const headlineTail = w.headline_tail || "meets craft.";
  const liveCols = Array.isArray(w.columns) && w.columns.length
    ? w.columns
    : [
        { title: "The plan, never the gimmick.",
          body: "Data-driven strategies that align with your business goals and audience behaviour. Every move is built for measurable growth and long-term impact — actionable, not aspirational." },
        { title: "Channels that compound.",
          body: "Email, paid, organic, and creative — calibrated together so each channel fuels the next. Content that informs, inspires, and sells. Campaigns built to be measured, not just launched." },
      ];
  return (
  <section data-testid="why-us" className="relative bg-[#0B0F14]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 mb-12 md:mb-16">
        <div className="lg:col-span-5">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>.{" "}
                </>
              ) : null}
              {headlineMiddle ? <>{headlineMiddle} </> : null}
              <PillarAccent>{headlineAccent}</PillarAccent>{headlineTail ? <> {headlineTail}</> : null}
            </span>
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        {liveCols.map((col, i) => (
          <div key={i} data-reveal="mask-block" style={{ "--i": i }}>
            <h3 className="font-display text-[1.25rem] md:text-[1.4rem] font-black tracking-[-0.015em] text-white leading-[1.2] mb-4">
              {col.title}
            </h3>
            <p className="text-[1rem] md:text-[1.075rem] text-neutral-400 leading-[1.65] max-w-[52ch]">
              {col.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   04 · Method — sticky-stacking 6-step cards (signature)
============================================================ */
const METHOD = [
  {
    n: "01",
    eyebrow: "Listen",
    title: "We learn your business before we build for it.",
    body: "Workshops, audits, customer interviews, analytics deep-dives. Nothing gets shipped until we understand what's actually moving the needle.",
    kpis: ["Audit & analytics", "Audience research", "Goal alignment"],
  },
  {
    n: "02",
    eyebrow: "Strategize",
    title: "A roadmap with milestones, not just opinions.",
    body: "Channel mix, budget allocation, content calendar, and the experiments we'll run. Decisions documented before a dollar is spent.",
    kpis: ["Channel strategy", "Budget plan", "Test roadmap"],
  },
  {
    n: "03",
    eyebrow: "Create",
    title: "Creative that earns the click — and the second one.",
    body: "Copy, design, video, and motion built around the strategy. No template ads, no recycled hooks. Made-for-channel from the start.",
    kpis: ["Ad creative", "Email design", "Landing pages"],
  },
  {
    n: "04",
    eyebrow: "Launch",
    title: "Coordinated rollouts across every active channel.",
    body: "Paid, organic, email, influencer, lifecycle — orchestrated to fire together. Tracking and attribution wired in from day one.",
    kpis: ["Cross-channel ops", "Tracking setup", "QA pre-launch"],
  },
  {
    n: "05",
    eyebrow: "Measure",
    title: "Performance dashboards your team can actually read.",
    body: "Weekly reviews, monthly deep-dives. We focus on the numbers that compound — LTV, CAC, return rate, repeat purchase — not vanity reach.",
    kpis: ["Weekly reporting", "LTV / CAC", "Channel attribution"],
  },
  {
    n: "06",
    eyebrow: "Iterate",
    title: "Optimize, double down, kill what isn't working.",
    body: "We run our own experiments and report results candidly. The plan changes — what doesn't is the discipline of measuring before deciding.",
    kpis: ["A/B testing", "Creative refresh", "Quarterly review"],
  },
];

/* Tiny animated KPI markers — replace static dots with subtle motion */
const KpiMarker = ({ kind, i }) => {
  if (kind === "bar") {
    return (
      <span className="relative flex-shrink-0 w-5 h-1 rounded-full bg-white/[0.08] overflow-hidden">
        <motion.span
          className="absolute inset-0 rounded-full bg-[#F4D64E]"
          initial={{ x: "-100%" }}
          animate={{ x: ["-100%", "0%", "0%", "100%"] }}
          transition={{
            duration: 3,
            ease: [0.22, 1, 0.36, 1],
            repeat: Infinity,
            delay: i * 0.2,
            times: [0, 0.4, 0.7, 1],
          }}
        />
      </span>
    );
  }
  if (kind === "pulse") {
    return (
      <span className="relative flex-shrink-0 w-2 h-2">
        <motion.span
          className="absolute inset-0 rounded-full bg-[#F4D64E]"
          animate={{ scale: [1, 1.6, 1], opacity: [1, 0, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
        />
        <span className="absolute inset-0.5 rounded-full bg-[#F4D64E]" />
      </span>
    );
  }
  if (kind === "ring") {
    return (
      <span className="relative flex-shrink-0 w-4 h-4">
        <motion.span
          className="absolute inset-0 rounded-full border border-[#F4D64E]"
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear", delay: i * 0.4 }}
          style={{
            background: "conic-gradient(from 0deg, #F4D64E, transparent 60%)",
            mask: "radial-gradient(circle, transparent 50%, black 51%)",
            WebkitMask: "radial-gradient(circle, transparent 50%, black 51%)",
          }}
        />
        <span className="absolute inset-[5px] rounded-full bg-[#F4D64E]" />
      </span>
    );
  }
  // default: ticking digits
  return (
    <span className="relative flex-shrink-0 w-5 h-5 inline-flex items-center justify-center text-[0.6rem] font-bold tabular-nums text-[#F4D64E]">
      <motion.span
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: i * 0.25 }}
      >
        ↗
      </motion.span>
    </span>
  );
};

const MARKER_KINDS = ["bar", "pulse", "ring", "bar", "pulse", "ring"];

const MethodCard = ({ step, i, total, kpiLabel }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // 3D depth — cards tilt back and recede as user scrolls past
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.88]);
  const opacity = useTransform(scrollYProgress, [0, 0.6, 1], [1, 0.7, 0.45]);
  const rotateX = useTransform(scrollYProgress, [0, 1], [0, -6]);
  const translateY = useTransform(scrollYProgress, [0, 1], [0, -30]);
  const markerKind = MARKER_KINDS[i % MARKER_KINDS.length];

  return (
    <div
      ref={ref}
      className="relative bg-[#0B0F14] py-10 lg:py-0 lg:sticky lg:top-0 lg:h-screen lg:flex lg:items-center"
      style={{ zIndex: i + 1, perspective: "1600px" }}
    >
      <motion.div
        style={
          typeof window !== "undefined" && window.innerWidth >= 1024
            ? {
                scale,
                opacity,
                rotateX,
                y: translateY,
                transformStyle: "preserve-3d",
              }
            : undefined
        }
        className="w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-10"
      >
        <div className="relative rounded-2xl lg:rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#11171F] to-[#0D131A] overflow-hidden shadow-[0_60px_120px_-40px_rgba(0,0,0,0.85)] lg:min-h-[78vh] flex">
          {/* Ambient drifting grid backdrop */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-[0.5]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(244,214,78,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(244,214,78,0.045) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
              maskImage:
                "radial-gradient(700px 500px at 80% 30%, black, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(700px 500px at 80% 30%, black, transparent 75%)",
            }}
          />
          {/* Floating yellow glow that drifts left to right */}
          <motion.div
            aria-hidden
            className="absolute top-0 -left-32 w-[24rem] h-[24rem] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(244,214,78,0.10), transparent 60%)" }}
            animate={{ x: ["0%", "180%", "0%"] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* GIANT numeral — full card height on the right side */}
          <div
            aria-hidden
            className="absolute right-[-2%] sm:right-[-1%] top-1/2 -translate-y-1/2 pointer-events-none select-none leading-none"
          >
            <span
              className="block font-display font-black tracking-[-0.06em] leading-[0.78] text-[#F4D64E]/[0.08]"
              style={{ fontSize: "clamp(12rem, 28vw, 28rem)" }}
            >
              {step.n}
            </span>
          </div>

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-y-0 w-full">
            {/* LEFT — copy */}
            <div className="lg:col-span-7 p-7 sm:p-9 md:p-12 lg:p-14 lg:pr-10 flex flex-col justify-center gap-7 lg:gap-9 min-h-full">
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                <span className="text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] tabular-nums">
                  {step.n} / {String(total).padStart(2, "0")}
                </span>
                <span className="hidden sm:inline h-px flex-1 bg-white/[0.06] max-w-[100px]" />
                <span className="text-[0.65rem] sm:text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                  {step.eyebrow}
                </span>
              </div>
              <h3 className="font-display text-[1.7rem] sm:text-[2.1rem] md:text-[2.6rem] lg:text-[3.25rem] xl:text-[3.75rem] font-black tracking-[-0.04em] leading-[1.0] text-white">
                {step.title}
              </h3>
              <p className="text-[0.95rem] md:text-[1.05rem] lg:text-[1.15rem] text-neutral-300 leading-[1.6] max-w-[56ch]">
                {step.body}
              </p>
            </div>

            {/* RIGHT — KPI list with animated markers */}
            <div className="lg:col-span-5 p-7 sm:p-9 md:p-12 lg:p-14 lg:border-l border-t lg:border-t-0 border-white/[0.06] bg-[#0B0F14]/70 backdrop-blur-sm relative z-10 flex flex-col justify-center">
              <div className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-6">
                {kpiLabel || "What ships at this stage"}
              </div>
              <ul className="flex flex-col">
                {step.kpis.map((k, kIdx) => (
                  <li
                    key={k}
                    className="flex items-center gap-4 py-4 border-b border-white/[0.06] last:border-b-0 text-[1rem] md:text-[1.05rem] text-neutral-100"
                  >
                    <KpiMarker kind={markerKind} i={kIdx} />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const MethodIntro = ({ eyebrow, headlineMain, headlineItalic, headlineAccent, description }) => (
  <section className="relative bg-[#0B0F14] border-t border-white/[0.06]">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="max-w-4xl">
        <span data-reveal="eyebrow">
          <Eyebrow>{eyebrow}</Eyebrow>
        </span>
        <h2
          data-reveal="mask-block"
          style={{ "--i": 1 }}
          className="mt-7 font-display text-[2.5rem] md:text-[4rem] lg:text-[4.75rem] font-black tracking-[-0.04em] leading-[0.98] text-white"
        >
          <span>
            {headlineMain}{" "}
            {headlineItalic ? (
              <>
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
              </>
            ) : null}
            <PillarAccent>{headlineAccent}</PillarAccent>.
          </span>
        </h2>
        <p
          data-reveal="mask-block"
          style={{ "--i": 2 }}
          className="mt-10 max-w-[60ch] text-[1.05rem] md:text-[1.15rem] text-neutral-400 leading-[1.65]"
        >
          <span>{description}</span>
        </p>
      </div>
    </div>
  </section>
);

const Method = () => {
  const cms = useContent("marketing");
  const m = cms?.method || {};
  const eyebrow = m.intro_eyebrow || "Our method";
  const headlineMain = m.headline_main || "Six steps,";
  const headlineItalic = m.headline_italic || "measured";
  const headlineAccent = m.headline_accent || "every week";
  const description = m.intro_description ||
    "The way we run every engagement — same rhythm whether we're shipping a 2-week campaign or a multi-quarter growth program.";
  const kpiLabel = m.kpi_label || "What ships at this stage";
  const liveSteps = Array.isArray(m.items) && m.items.length
    ? m.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        eyebrow: it.tag_label || "",
        title: it.title || "",
        body: it.body || "",
        kpis: Array.isArray(it.highlights) ? it.highlights : [],
      }))
    : METHOD;
  return (
  <section data-testid="method" className="relative bg-[#0B0F14]">
    <MethodIntro
      eyebrow={eyebrow}
      headlineMain={headlineMain}
      headlineItalic={headlineItalic}
      headlineAccent={headlineAccent}
      description={description}
    />
    <div className="relative">
      {liveSteps.map((s, i) => (
        <MethodCard key={i} step={s} i={i} total={liveSteps.length} kpiLabel={kpiLabel} />
      ))}
    </div>
    <div className="hidden lg:block lg:h-[20vh]" />
  </section>
  );
};

/* ============================================================
   05 · 8 services — staggered editorial grid with hover media
============================================================ */
const SERVICES = [
  {
    n: "01",
    title: "Strategy & Consulting",
    body: "Data-driven strategies that align with your business goals — every move built for measurable growth and long-term impact.",
    img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "02",
    title: "Content Creation",
    body: "Engaging product visuals, copy, and digital assets that enhance the buying journey and boost conversions.",
    img: "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "03",
    title: "Social Media",
    body: "Strategic content calendars and targeted campaigns that turn followers into loyal customers and revenue.",
    img: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "04",
    title: "Email Marketing",
    body: "Targeted, automated, personalized flows — from welcome series to lifecycle. Segmentation that maximizes engagement.",
    img: "https://images.unsplash.com/photo-1633265486064-086b219458ec?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "05",
    title: "Influencer & Collabs",
    body: "Authentic voice partnerships that boost engagement, build trust, and convert followers into loyal customers.",
    img: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "06",
    title: "Paid Marketing",
    body: "Targeted Meta, TikTok, Pinterest, Google Search & Shopping campaigns — precise audiences, optimized creatives, measurable ROI.",
    img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "07",
    title: "SEO",
    body: "Comprehensive audits and competitive analysis — optimize every stage of the funnel and drive qualified traffic that converts.",
    img: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?auto=format&fit=crop&w=1200&q=70",
  },
  {
    n: "08",
    title: "Conversion Optimization",
    body: "User-behaviour analysis, iterative testing, and journey optimization. Turn more visitors into buyers with data-driven changes.",
    img: "https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=1200&q=70",
  },
];

const ServicesGrid = () => {
  const cms = useContent("marketing");
  const s = cms?.services_grid || {};
  const eyebrow = s.eyebrow || "Eight disciplines, one team";
  const headlineMain = s.headline_main || "Every channel";
  const headlineItalic = s.headline_italic || "that";
  const headlineAccent = s.headline_accent || "moves metrics";
  const description = s.description ||
    "Strategy, creative, and media — calibrated together so each channel makes the next one work harder.";
  const ctaHref = s.cta_href || "#contact";
  const liveItems = Array.isArray(s.items) && s.items.length
    ? s.items.map((it, i) => ({
        n: it.tag_number || String(i + 1).padStart(2, "0"),
        title: it.title || "",
        body: it.body || "",
        img: it.media || "",
      }))
    : SERVICES;
  return (
  <section
    id="services"
    data-testid="services"
    className="relative bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-12 mb-16 md:mb-20">
        <div className="lg:col-span-5">
          <span data-reveal="eyebrow">
            <Eyebrow>{eyebrow}</Eyebrow>
          </span>
          <h2
            data-reveal="mask-block"
            style={{ "--i": 1 }}
            className="mt-7 font-display text-[2.25rem] md:text-[3rem] lg:text-[3.5rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
          >
            <span>
              {headlineMain}{" "}
              {headlineItalic ? (
                <>
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>{" "}
                </>
              ) : null}
              <PillarAccent>{headlineAccent}</PillarAccent>.
            </span>
          </h2>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 self-end">
          <p
            data-reveal="mask-block"
            style={{ "--i": 2 }}
            className="text-[1.02rem] md:text-[1.125rem] text-neutral-400 leading-[1.65]"
          >
            <span>{description}</span>
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16 md:gap-y-20">
        {liveItems.map((item, i) => (
          <li
            key={i}
            id={`service-${item.n}`}
            data-reveal="mask-block"
            style={{ "--i": Math.min(i, 5) }}
            data-testid={`service-${item.n}`}
            className={`group scroll-mt-24 ${i % 2 === 1 ? "md:translate-y-16" : ""}`}
          >
            <a href={ctaHref} className="block">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0F141A] mb-7 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:border-[#F4D64E]/30">
                {item.img ? (
                  <img
                    src={item.img}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  />
                ) : null}
                <span
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-[#0B0F14]/80 via-[#0B0F14]/20 to-transparent transition-opacity duration-500 group-hover:from-[#0B0F14]/60"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 ring-0 group-hover:ring-1 ring-inset ring-[#F4D64E]/20 rounded-2xl transition-all duration-500"
                />
                {/* Numeric tag overlay */}
                <span className="absolute top-5 left-5 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-[#F4D64E] tabular-nums">
                  {item.n}
                </span>
                {/* Hover arrow chip */}
                <span className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight className="w-4 h-4" />
                </span>
              </div>
              <h3 className="font-display text-[1.45rem] md:text-[1.65rem] lg:text-[1.85rem] font-black tracking-[-0.02em] leading-[1.15] text-white group-hover:text-[#F4D64E] transition-colors mb-3">
                {item.title}
              </h3>
              <p className="text-[0.98rem] md:text-[1.02rem] text-neutral-400 leading-[1.6] max-w-[52ch]">
                {item.body}
              </p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  </section>
  );
};

/* ============================================================
   06 · Showreel band — full-bleed coding-equivalent for marketing
============================================================ */
const SHOWREEL_VIDEO =
  "https://videos.pexels.com/video-files/3209828/3209828-hd_1920_1080_25fps.mp4";

// Quick URL → media-kind detector. Mirrors `mediaKindFromUrl` in App.js.
const showreelMediaKind = (url) => {
  if (!url) return "video";
  const clean = String(url).split("?")[0].split("#")[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogv)$/.test(clean)) return "video";
  if (/\.(png|jpe?g|webp|gif|svg|avif|heic|heif)$/.test(clean)) return "image";
  return "video"; // unknown → treat as video to preserve current behavior
};

const Showreel = () => {
  const cms = useContent("marketing");
  const s = cms?.showreel || {};
  const eyebrow = s.eyebrow || "Behind the work";
  const headlineMain = s.headline_main || "We move metrics —";
  const headlineItalic = s.headline_italic || "not";
  const headlineAccent = s.headline_accent || "vanity numbers";
  const description = s.description ||
    "Strategy, creative, paid media, and analytics calibrated together so the numbers that matter — LTV, CAC, repeat purchase, return rate — actually compound.";
  // Background-media resolution rule:
  //   1. If admin uploaded a video → play that video, use poster (if any)
  //      as the loading placeholder.
  //   2. Else if admin uploaded an image (in EITHER `video` OR `poster`
  //      slot, since users frequently confuse the two) → render that
  //      image full-bleed.
  //   3. Else → fall back to the hardcoded SHOWREEL_VIDEO.
  const videoCandidate = s.video || "";
  const posterCandidate = s.poster || "";
  const videoKind = showreelMediaKind(videoCandidate);
  const posterKind = showreelMediaKind(posterCandidate);

  let media;
  let kind;
  let videoPoster = "";
  if (videoCandidate && videoKind === "video") {
    media = videoCandidate;
    kind = "video";
    // Use the poster slot when it's an actual image.
    videoPoster = posterCandidate && posterKind === "image"
      ? posterCandidate
      : "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2000&q=70";
  } else if (videoCandidate && videoKind === "image") {
    // Admin uploaded an image into the "video" slot. Render <img>.
    media = videoCandidate;
    kind = "image";
  } else if (posterCandidate && posterKind === "image") {
    // No video uploaded; admin uploaded an image as poster — use it as
    // the full background still. This matches user intent: any image
    // dropped into this section should appear as the visible media.
    media = posterCandidate;
    kind = "image";
  } else if (!cms.__ready) {
    // CMS still resolving — show a clean black backdrop instead of
    // briefly flashing the hardcoded Pexels placeholder. This stops
    // the wrong URL from being fetched + cached on cold loads.
    media = "";
    kind = "image";
  } else {
    // CMS resolved + admin has not uploaded media — keep the original
    // hardcoded showreel as a permanent fallback.
    media = SHOWREEL_VIDEO;
    kind = "video";
    videoPoster = "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=2000&q=70";
  }
  return (
  <section
    data-testid="showreel"
    className="relative overflow-hidden bg-[#0B0F14] border-t border-white/[0.06]"
  >
    <div className="relative aspect-[16/10] md:aspect-[21/9] lg:aspect-[24/9] w-full">
      {!media ? (
        // Skeleton — CMS still loading. Render a clean black-on-black
        // backdrop instead of pulling a placeholder image off the
        // network.
        <div className="absolute inset-0 bg-[#0B0F14]" aria-hidden />
      ) : kind === "video" ? (
        <video
          src={media}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={videoPoster}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={media}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/92 via-[#0B0F14]/30 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/30 via-transparent to-[#0B0F14]/40"
      />

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className="max-w-3xl">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-7 font-display text-[2rem] md:text-[3.25rem] lg:text-[4.25rem] font-black tracking-[-0.035em] leading-[1.0] text-white"
            >
              <span>
                {headlineMain}{" "}
                {headlineItalic ? (
                  <>
                    <span className="italic font-normal text-neutral-300">{headlineItalic}</span>{" "}
                  </>
                ) : null}
                <PillarAccent>{headlineAccent}</PillarAccent>.
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-7 max-w-[52ch] text-[1rem] md:text-[1.1rem] text-neutral-300 leading-[1.6]"
            >
              <span>{description}</span>
            </p>
          </div>
        </div>
      </div>

      <span
        aria-hidden
        className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#F4D64E]/[0.06] blur-3xl pointer-events-none"
      />
    </div>
  </section>
  );
};

/* ============================================================
   PAGE
============================================================ */
export default function MarketingPage() {
  const { open: openContact } = useContactPanel();

  useSeo({
    route: "/ecommerce-marketing-agency",
    fallback: {
      title: "Marketing 360° — Sunday Digital",
      description: "Performance, brand, lifecycle, and creative — one studio, one accountable team.",
    },
  });
  useEffect(() => {
    document.title = "eCommerce Marketing — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reveal observer — same setup as HomeV5 / ShopifyDev
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="marketing-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <MarketingHero onPrimary={openContact} />
        <TrustStripMkt />
        <WhyUs />
        <Method />
        <ServicesGrid />
        <Showreel />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
